import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { API_BASE_URL } from '../config/api';
import { SearchableCustomerSelect } from './SearchableCustomerSelect';
import { PayableSelect } from './PayableSelect';
import { dollarsToCents, centsToAmountString } from '../utils/money';

export function ReconciliationMatchModal({ show, payment, apiCall, onHide, onMatch }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Create new payment form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customerId, setCustomerId] = useState('');
  // Multi-transaction allocations: [{ transaction_id, amount }]
  const [allocations, setAllocations] = useState([{ transaction_id: '', amount: '' }]);

  // Expanded suggestion (show transactions for a customer)
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);

  useEffect(() => {
    if (!show || !payment) return;

    const fetchSuggestions = async () => {
      setLoadingSuggestions(true);
      try {
        const result = await apiCall(`${API_BASE_URL}/reconciliation/suggestions/${payment.id}`);
        setSuggestions(result.data || []);
      } catch (err) {
        setError(err.message || 'Failed to load suggestions');
      } finally {
        setLoadingSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [show, payment, apiCall]);

  // Select a suggested customer — auto-populate the create form
  const selectSuggestedCustomer = (suggestion) => {
    setCustomerId(suggestion.customer_id);
    setShowCreateForm(true);

    // Auto-allocate unpaid transactions up to payment amount
    const paymentCents = dollarsToCents(payment.amount);
    let remaining = paymentCents;
    const autoAllocations = [];

    for (const tx of suggestion.unpaid_transactions) {
      if (remaining <= 0) break;
      const allocAmt = Math.min(dollarsToCents(tx.remaining_balance), remaining);
      autoAllocations.push({
        transaction_id: tx.id,
        amount: centsToAmountString(allocAmt),
      });
      remaining -= allocAmt;
    }

    if (autoAllocations.length === 0) {
      autoAllocations.push({ transaction_id: '', amount: centsToAmountString(paymentCents) });
    }

    setAllocations(autoAllocations);
  };

  // Add a new allocation row
  const addAllocation = () => {
    setAllocations([...allocations, { transaction_id: '', amount: '' }]);
  };

  // Remove an allocation row
  const removeAllocation = (index) => {
    if (allocations.length <= 1) return;
    setAllocations(allocations.filter((_, i) => i !== index));
  };

  // Update an allocation row. Use a functional update so consecutive calls
  // (e.g. setting transaction_id then amount in onSelectTransaction) compose
  // instead of clobbering each other via a stale `allocations` closure.
  const updateAllocation = (index, field, value) => {
    setAllocations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Calculate allocated vs total
  const totalAllocatedCents = allocations.reduce((sum, a) => sum + (dollarsToCents(a.amount) || 0), 0);
  const paymentCents = dollarsToCents(payment.amount);
  const remainingCents = paymentCents - totalAllocatedCents;
  const totalAllocated = totalAllocatedCents / 100;
  const paymentAmount = paymentCents / 100;
  const remaining = remainingCents / 100;

  const handleCreateAndMatch = async (e) => {
    e.preventDefault();
    if (!customerId) {
      setError('Please select a customer');
      return;
    }

    const validAllocations = allocations.filter(a => a.transaction_id && parseFloat(a.amount) > 0);

    if (validAllocations.length === 0) {
      setError('Please select at least one transaction and enter an amount');
      return;
    }

    if (remainingCents !== 0) {
      setError(`Allocated total ($${totalAllocated.toFixed(2)}) must equal payment amount ($${paymentAmount.toFixed(2)}). Remaining: $${remaining.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await apiCall(`${API_BASE_URL}/reconciliation/${payment.id}/match`, {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          transactions: validAllocations.map(a => ({
            transaction_id: parseInt(a.transaction_id),
            amount: centsToAmountString(dollarsToCents(a.amount)),
          })),
        }),
      });
      onMatch();
    } catch (err) {
      setError(err.message || 'Failed to create and match payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    const variants = { previous: 'info', high: 'success', medium: 'warning', low: 'secondary' };
    const labels = { previous: 'previously matched' };
    return <Badge bg={variants[confidence] || 'secondary'}>{labels[confidence] || confidence}</Badge>;
  };

  const formatDate = (d) => d ? d.split('T')[0] : '-';

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Match Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {/* Extracted payment details */}
        <div className="mb-4 p-3 bg-light rounded">
          <Row>
            <Col md={3}><strong>Sender:</strong> {payment.sender_name}</Col>
            <Col md={3}><strong>Amount:</strong> ${parseFloat(payment.amount).toFixed(2)}</Col>
            <Col md={3}><strong>Date:</strong> {formatDate(payment.payment_date)}</Col>
            <Col md={3}><strong>Type:</strong> <Badge bg={payment.payment_type === 'zelle' ? 'primary' : 'success'}>{payment.payment_type === 'zelle' ? 'Zelle' : 'Cash App'}</Badge></Col>
          </Row>
          {payment.memo && (
            <Row className="mt-2">
              <Col><strong>Memo:</strong> {payment.memo}</Col>
            </Row>
          )}
        </div>

        {/* Customer suggestions */}
        <h6>Suggested Customers</h6>
        {loadingSuggestions ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading suggestions...</div>
        ) : suggestions.length === 0 ? (
          <Alert variant="info">No customer name matches found. Use the form below to search manually.</Alert>
        ) : (
          <div className="mb-4">
            {suggestions.map((s) => (
              <div key={s.customer_id} className="border rounded mb-2">
                <div
                  className="d-flex justify-content-between align-items-center p-2"
                  style={{ cursor: 'pointer', backgroundColor: customerId === s.customer_id ? '#e7f3ff' : 'transparent' }}
                  onClick={() => setExpandedCustomerId(expandedCustomerId === s.customer_id ? null : s.customer_id)}
                >
                  <div>
                    <strong>{s.first_name} {s.last_name}</strong>
                    <span className="ms-2">{getConfidenceBadge(s.confidence)}</span>
                    {s.phone_number && <small className="text-muted ms-2">{s.phone_number}</small>}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted">
                      {s.unpaid_transactions.length} unpaid txn{s.unpaid_transactions.length !== 1 ? 's' : ''} — ${s.total_outstanding.toFixed(2)} owed
                    </small>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); selectSuggestedCustomer(s); }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
                {expandedCustomerId === s.customer_id && s.unpaid_transactions.length > 0 && (
                  <Table size="sm" className="mb-0 border-top">
                    <thead>
                      <tr>
                        <th>Txn #</th>
                        <th>Vehicle</th>
                        <th>Total</th>
                        <th>Remaining</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.unpaid_transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td>#{tx.id}</td>
                          <td><small>{tx.make ? `${tx.year} ${tx.make} ${tx.model}` : '-'}</small></td>
                          <td>${(parseFloat(tx.amount) + parseFloat(tx.tax_amount)).toFixed(2)}</td>
                          <td className="text-danger">${tx.remaining_balance.toFixed(2)}</td>
                          <td><small>{formatDate(tx.start_date)}</small></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create/allocate payment form */}
        <hr />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Allocate Payment</h6>
          {!showCreateForm && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => { setShowCreateForm(true); }}
            >
              Manual Entry
            </Button>
          )}
        </div>

        {showCreateForm && (
          <Form onSubmit={handleCreateAndMatch}>
            <Row className="mb-3">
              <Col md={6}>
                <SearchableCustomerSelect
                  apiCall={apiCall}
                  selectedCustomerId={customerId}
                  onSelectCustomer={(id) => {
                    setCustomerId(id);
                    setAllocations([{ transaction_id: '', amount: '' }]);
                  }}
                  required={true}
                />
              </Col>
              <Col md={6}>
                <div className="p-2 bg-light rounded">
                  <small>
                    <strong>Payment:</strong> ${paymentAmount.toFixed(2)} |{' '}
                    <strong>Allocated:</strong> ${totalAllocated.toFixed(2)} |{' '}
                    <span className={Math.abs(remaining) < 0.01 ? 'text-success' : 'text-danger'}>
                      <strong>Remaining:</strong> ${remaining.toFixed(2)}
                    </span>
                  </small>
                </div>
              </Col>
            </Row>

            {/* Transaction allocations */}
            <Form.Label>Transactions</Form.Label>
            {allocations.map((alloc, index) => (
              <Row key={index} className="mb-2 align-items-end">
                <Col md={7}>
                  <PayableSelect
                    apiCall={apiCall}
                    reconciliationId={payment.id}
                    customerId={customerId}
                    selectedId={alloc.transaction_id}
                    excludeIds={allocations
                      .filter((_, i) => i !== index)
                      .map((a) => a.transaction_id)}
                    onSelect={(item) => {
                      const txId = item?.id || '';
                      const remainingBalance = item ? dollarsToCents(item.remaining_balance) / 100 : 0;
                      updateAllocation(index, 'transaction_id', txId);
                      if (txId && remainingBalance > 0) {
                        const otherAllocated = allocations.reduce((sum, a, i) => i === index ? sum : sum + (parseFloat(a.amount) || 0), 0);
                        const availableToAllocate = Math.round((paymentAmount - otherAllocated) * 100) / 100;
                        const autoAmount = Math.min(remainingBalance, availableToAllocate);
                        updateAllocation(index, 'amount', autoAmount > 0 ? autoAmount.toFixed(2) : '');
                      }
                    }}
                    required={false}
                  />
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-md-none">Amount</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={alloc.amount}
                      onChange={(e) => updateAllocation(index, 'amount', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <div className="mb-3">
                    {allocations.length > 1 && (
                      <Button variant="outline-danger" size="sm" onClick={() => removeAllocation(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            ))}

            <div className="mb-3">
              <Button variant="outline-secondary" size="sm" onClick={addAllocation}>
                + Add Transaction
              </Button>
            </div>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date</Form.Label>
                  <Form.Control type="text" value={formatDate(payment.payment_date)} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Method</Form.Label>
                  <Form.Control type="text" value={payment.payment_type === 'zelle' ? 'Zelle' : 'Cash App'} disabled />
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" variant="primary" disabled={submitting || !customerId || Math.abs(remaining) > 0.01}>
              {submitting ? <><Spinner animation="border" size="sm" /> Creating...</> : `Create ${allocations.filter(a => a.transaction_id).length > 1 ? allocations.filter(a => a.transaction_id).length + ' Payments' : 'Payment'} & Match`}
            </Button>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReconciliationMatchModal;
